import React, { useCallback, useEffect, useState, useRef } from 'react';
import { MicIcon, SquareIcon, PenLineIcon, FileUpIcon, LoaderIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import OpenAI from 'openai';
import { VOICE_TRANSCRIPTION_CLEANUP_PROMPT, TASK_VALIDATION_PROMPT } from '../../config/aiPrompts';
import { useI18n } from '../../config/i18n';
const STORAGE_KEY = 'ptp_openai_api_key';
function getSavedApiKey(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}
function saveApiKey(key: string) {
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch {}
}
type InputMode = 'voice' | 'text' | 'upload';
function getOpenAIClient(): OpenAI {
  const apiKey = getSavedApiKey();
  return new OpenAI({
    apiKey: apiKey || 'missing-key',
    dangerouslyAllowBrowser: true
  });
}
export function VoiceDescriptionScreen({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const {
    lang,
    t
  } = useI18n();
  const [mode, setMode] = useState<InputMode>('voice');
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(data.taskDescription.length > 0);
  const [transcription, setTranscription] = useState(data.taskDescription);
  const [textInput, setTextInput] = useState(data.taskDescription || '');
  const [audioLevel, setAudioLevel] = useState(0);
  const [showKeyInput, setShowKeyInput] = useState(!getSavedApiKey());
  const [keyInput, setKeyInput] = useState(getSavedApiKey());
  const [missingInfo, setMissingInfo] = useState<string[]>([]);
  const [isAnalyzingText, setIsAnalyzingText] = useState(false);
  const [textAnalyzed, setTextAnalyzed] = useState(false);
  const appendModeRef = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animFrameRef = useRef<number>(0);
  // Audio level monitoring
  const startAudioMonitoring = useCallback((stream: MediaStream) => {
    try {
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
      analyserRef.current = analyser;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(dataArray);
        // Compute RMS volume
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i] * dataArray[i];
        }
        const rms = Math.sqrt(sum / dataArray.length);
        const normalized = Math.min(rms / 128, 1); // 0..1
        setAudioLevel(normalized);
        animFrameRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) {
      console.error('Audio monitoring failed:', err);
    }
  }, []);
  const stopAudioMonitoring = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = 0;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAudioMonitoring();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, [stopAudioMonitoring]);
  const [micError, setMicError] = useState<string | null>(null);
  const startRecording = useCallback(async () => {
    setMicError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      streamRef.current = stream;
      chunksRef.current = [];
      // Start audio level monitoring
      startAudioMonitoring(stream);
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
      });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      mediaRecorder.start(250);
      setIsRecording(true);
      if (!appendModeRef.current) {
        setTranscription('');
        setHasRecorded(false);
      }
    } catch (err) {
      console.error('Microphone access denied:', err);
      setMicError('Microphone access denied. Please allow microphone permission and try again.');
    }
  }, [startAudioMonitoring]);
  const stopRecording = useCallback(async () => {
    stopAudioMonitoring();
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      setIsRecording(false);
      setIsTranscribing(false);
      setMicError('Recording failed — no active recorder. Please try again.');
      return;
    }
    setIsRecording(false);
    setIsTranscribing(true);
    setMicError(null);
    const audioBlob = await new Promise<Blob>(resolve => {
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: 'audio/webm'
        });
        resolve(blob);
      };
      mediaRecorder.stop();
    });
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    // Check if we actually captured audio
    if (audioBlob.size < 1000) {
      setIsTranscribing(false);
      setMicError(`Recording too short or empty (${audioBlob.size} bytes). Speak for at least 2 seconds.`);
      return;
    }
    try {
      const file = new File([audioBlob], 'recording.webm', {
        type: 'audio/webm'
      });
      console.log(`[Whisper] Sending audio: ${(audioBlob.size / 1024).toFixed(1)}KB, type: ${audioBlob.type}`);
      const response = await getOpenAIClient().audio.transcriptions.create({
        model: 'whisper-1',
        file,
        language: 'en'
      });
      const rawText = response.text.trim();
      console.log('[Whisper] Raw transcription:', rawText);
      if (!rawText) {
        setIsTranscribing(false);
        setMicError('No speech detected. Please speak clearly and try again.');
        return;
      }
      // Analyze and improve the transcription into a clear task description
      let correctedText = rawText;
      try {
        const completion = await getOpenAIClient().chat.completions.create({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: VOICE_TRANSCRIPTION_CLEANUP_PROMPT
          }, {
            role: 'user',
            content: rawText
          }],
          temperature: 0.2,
          max_tokens: 400
        });
        correctedText = completion.choices[0]?.message?.content?.trim() || rawText;
      } catch (correctionErr) {
        console.warn('[Grammar] Correction failed, using raw text:', correctionErr);
        correctedText = rawText;
      }
      const finalText = appendModeRef.current && transcription ? `${transcription} ${correctedText}` : correctedText;
      appendModeRef.current = false;
      setTranscription(finalText);
      setHasRecorded(true);
      setIsTranscribing(false);
      updateData({
        taskDescription: finalText
      });
      // Validate task description for completeness — use finalText (combined) not just the new snippet
      setMissingInfo([]);
      try {
        const valResult = await getOpenAIClient().chat.completions.create({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: TASK_VALIDATION_PROMPT
          }, {
            role: 'user',
            content: finalText
          }],
          temperature: 0,
          max_tokens: 60
        });
        const valRaw = valResult.choices[0]?.message?.content?.trim() || '';
        let valJson = valRaw;
        const fenceMatch = valRaw.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (fenceMatch) valJson = fenceMatch[1].trim();
        const val = JSON.parse(valJson) as {
          hasTask?: boolean;
          hasLocation?: boolean;
          hasTime?: boolean;
        };
        const missing: string[] = [];
        if (!val.hasTask) missing.push('task description');
        if (!val.hasLocation) missing.push('location');
        if (!val.hasTime) missing.push('time / schedule');
        setMissingInfo(missing);
      } catch (valErr) {
        console.warn('[Validation] Check failed, skipping:', valErr);
      }
    } catch (err: any) {
      console.error('[Whisper] Transcription failed:', err);
      setIsTranscribing(false);
      // Show detailed error
      const errorMsg = err?.message || err?.error?.message || String(err);
      const statusCode = err?.status || err?.statusCode;
      if (statusCode === 401) {
        setMicError('OpenAI API key is invalid or expired. Please check your API key in the Integrations panel.');
      } else if (statusCode === 429) {
        setMicError('OpenAI rate limit reached. Please wait a moment and try again.');
      } else if (statusCode === 403) {
        setMicError('API key does not have access to the Whisper model. Check your OpenAI plan.');
      } else {
        setMicError(`Transcription failed: ${errorMsg}`);
      }
    }
  }, [updateData, stopAudioMonitoring]);
  const handleRecordToggle = () => {
    if (isRecording) {
      stopRecording();
    } else {
      appendModeRef.current = false;
      setMissingInfo([]);
      startRecording();
    }
  };
  // ── Analyze typed text through the same AI pipeline as voice ──
  const analyzeTextInput = useCallback(async () => {
    const raw = textInput.trim();
    if (!raw) return;
    setIsAnalyzingText(true);
    setMissingInfo([]);
    try {
      // Step 1: Clean up / improve the text (same as voice)
      let correctedText = raw;
      try {
        const completion = await getOpenAIClient().chat.completions.create({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: VOICE_TRANSCRIPTION_CLEANUP_PROMPT
          }, {
            role: 'user',
            content: raw
          }],
          temperature: 0.2,
          max_tokens: 400
        });
        correctedText = completion.choices[0]?.message?.content?.trim() || raw;
      } catch (correctionErr) {
        console.warn('[Text] Correction failed, using raw text:', correctionErr);
        correctedText = raw;
      }
      // Update state with cleaned text
      setTranscription(correctedText);
      setTextInput(correctedText);
      setHasRecorded(true);
      setTextAnalyzed(true);
      updateData({
        taskDescription: correctedText
      });
      // Step 2: Validate for completeness (same as voice)
      try {
        const valResult = await getOpenAIClient().chat.completions.create({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: TASK_VALIDATION_PROMPT
          }, {
            role: 'user',
            content: correctedText
          }],
          temperature: 0,
          max_tokens: 60
        });
        const valRaw = valResult.choices[0]?.message?.content?.trim() || '';
        let valJson = valRaw;
        const fenceMatch = valRaw.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (fenceMatch) valJson = fenceMatch[1].trim();
        const val = JSON.parse(valJson) as {
          hasTask?: boolean;
          hasLocation?: boolean;
          hasTime?: boolean;
        };
        const missing: string[] = [];
        if (!val.hasTask) missing.push(t('voice.missingTask'));
        if (!val.hasLocation) missing.push(t('voice.missingLocation'));
        if (!val.hasTime) missing.push(t('voice.missingTime'));
        setMissingInfo(missing);
      } catch (valErr) {
        console.warn('[Validation] Check failed, skipping:', valErr);
      }
    } catch (err: any) {
      console.error('[Text Analysis] Failed:', err);
    } finally {
      setIsAnalyzingText(false);
    }
  }, [textInput, updateData, t]);
  // Generate bar heights from audio level
  const NUM_BARS = 7;
  const bars = Array.from({
    length: NUM_BARS
  }, (_, i) => {
    const center = (NUM_BARS - 1) / 2;
    const dist = Math.abs(i - center) / center; // 0 at center, 1 at edges
    const height = Math.max(4, audioLevel * (1 - dist * 0.6) * 40);
    return height;
  });
  const modes: {
    id: InputMode;
    label: string;
    icon: typeof MicIcon;
  }[] = [{
    id: 'voice',
    label: t('voice.modeVoice'),
    icon: MicIcon
  }, {
    id: 'text',
    label: t('voice.modeType'),
    icon: PenLineIcon
  }, {
    id: 'upload',
    label: t('voice.modeUpload'),
    icon: FileUpIcon
  }];
  return <div className="flex flex-col h-full" data-id="element-1673">
      <div className="mb-5" data-id="element-1674">
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1675">
          {t('voice.title')}
        </h2>
        <p className="text-gray-500 text-sm" data-id="element-1676">{t('voice.subtitle')}</p>
        <p className="text-gray-400 text-xs mt-2 italic" data-id="element-1677">
          {t('voice.example')}
        </p>
      </div>

      {/* API Key Input */}
      {showKeyInput && <div className="mb-4 p-4 rounded-xl bg-white border border-amber-300" data-id="element-1678">
          <p className="text-amber-700 text-xs font-semibold mb-2" data-id="element-1679">
            OpenAI API Key Required
          </p>
          <p className="text-gray-500 text-xs mb-3" data-id="element-1680">
            Paste your OpenAI API key to enable voice transcription. It's stored
            locally on your device.
          </p>
          <div className="flex gap-2" data-id="element-1681">
            <input type="password" placeholder="sk-..." value={keyInput} onChange={e => setKeyInput(e.target.value)} className="flex-1 bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-yellow-400/50" data-id="element-1682" />
            <button onClick={() => {
          if (keyInput.trim()) {
            saveApiKey(keyInput.trim());
            setShowKeyInput(false);
            setMicError(null);
          }
        }} disabled={!keyInput.trim().startsWith('sk-')} className="px-4 py-2 bg-yellow-400 text-gray-900 text-xs font-bold rounded-lg disabled:opacity-40" data-id="element-1683">
              Save
            </button>
          </div>
          {getSavedApiKey() && <p className="text-green-600 text-xs mt-2" data-id="element-1684">✓ Key saved</p>}
        </div>}

      {!showKeyInput && <button onClick={() => setShowKeyInput(true)} className="mb-3 text-gray-400 text-xs underline self-end" data-id="element-1685">
          Change API key
        </button>}

      {/* Mode Selector */}
      <div className="flex bg-gray-100 border border-gray-200 rounded-xl p-1 mb-6 relative" data-id="element-1686">
        {modes.map(m => {
        const Icon = m.icon;
        const isActive = mode === m.id;
        return <button key={m.id} onClick={() => setMode(m.id)} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold transition-colors relative z-10 ${isActive ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-1687">
              {isActive && <motion.div layoutId="mode-bg" className="absolute inset-0 bg-white rounded-lg shadow-sm" transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} data-id="element-1688" />}
              <span className="relative z-10 flex items-center gap-1.5" data-id="element-1689">
                <Icon className="w-3.5 h-3.5" data-id="element-1690" />
                {m.label}
              </span>
            </button>;
      })}
      </div>

      {/* Voice Mode */}
      {mode === 'voice' && <div className="flex-1 flex flex-col items-center justify-center space-y-6" data-id="element-1691">
          {/* Record Button */}
          <div className="relative flex items-center justify-center" data-id="element-1692">
            {/* Audio level ring */}
            {isRecording && <motion.div className="absolute rounded-full border-2 border-yellow-400/60" animate={{
          width: 88 + audioLevel * 50,
          height: 88 + audioLevel * 50,
          opacity: 0.3 + audioLevel * 0.7
        }} transition={{
          duration: 0.1
        }} data-id="element-1693" />}
            {isRecording && <motion.div animate={{
          scale: [1, 1.15, 1]
        }} transition={{
          repeat: Infinity,
          duration: 1.5,
          delay: 0.2
        }} className="absolute w-24 h-24 bg-yellow-100 rounded-full" data-id="element-1694" />}
            {isTranscribing && <motion.div animate={{
          scale: [1, 1.1, 1]
        }} transition={{
          repeat: Infinity,
          duration: 1
        }} className="absolute w-28 h-28 bg-blue-100 rounded-full" data-id="element-1695" />}
            <motion.button whileTap={{
          scale: 0.9
        }} onClick={handleRecordToggle} disabled={isTranscribing} className={`relative z-10 w-20 h-20 rounded-full flex items-center justify-center shadow-xl transition-colors disabled:opacity-60 ${isTranscribing ? 'bg-blue-500 shadow-blue-400/30' : isRecording ? 'bg-red-500 shadow-red-400/30' : 'bg-yellow-400 shadow-yellow-400/30'}`} data-id="element-1696">
              {isTranscribing ? <motion.div animate={{
            rotate: 360
          }} transition={{
            repeat: Infinity,
            duration: 1,
            ease: 'linear'
          }} data-id="element-1697">
                  <LoaderIcon className="w-8 h-8 text-white" data-id="element-1698" />
                </motion.div> : isRecording ? <SquareIcon className="w-8 h-8 text-white fill-current" data-id="element-1699" /> : <MicIcon className="w-8 h-8 text-gray-900" data-id="element-1700" />}
            </motion.button>
          </div>

          {/* Audio Level Bars */}
          {isRecording && <div className="flex items-end justify-center gap-1 h-10" data-id="element-1701">
              {bars.map((h, i) => <motion.div key={i} className="w-1.5 rounded-full bg-yellow-400" animate={{
          height: h
        }} transition={{
          duration: 0.08
        }} data-id="element-1702" />)}
            </div>}

          {/* Status Text */}
          <div className="text-center" data-id="element-1703">
            <p className={`font-medium text-sm ${isTranscribing ? 'text-blue-600 animate-pulse' : isRecording ? 'text-red-500' : 'text-gray-500'}`} data-id="element-1704">
              {isTranscribing ? t('voice.analyzing') : isRecording ? t('voice.tapToStop') : hasRecorded ? t('voice.tapToReRecord') : t('voice.tapToRecord')}
            </p>
            {isRecording && audioLevel > 0.05 && <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="text-green-600 text-xs mt-1 font-medium" data-id="element-1705">
                ● Voice detected
              </motion.p>}
            {isRecording && audioLevel <= 0.05 && <p className="text-gray-400 text-xs mt-1" data-id="element-1706">Waiting for voice…</p>}
            {micError && <p className="text-red-500 text-xs mt-2 max-w-[250px] mx-auto" data-id="element-1707">
                {micError}
              </p>}
          </div>

          {/* Transcription Box — visible when transcribing or has result */}
          <div className={`w-full p-5 rounded-xl bg-gray-50 border border-gray-200 min-h-[100px] transition-all duration-300 ${hasRecorded || isTranscribing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`} data-id="element-1708">
            {isTranscribing ? <>
                <p className="text-xs text-blue-600 uppercase tracking-wider font-semibold mb-3" data-id="element-1709">
                  Analyzing description…
                </p>
                <div className="space-y-2" data-id="element-1710">
                  <div className="h-3 bg-gray-200 rounded-full w-full animate-pulse" data-id="element-1711" />
                  <div className="h-3 bg-gray-200 rounded-full w-4/5 animate-pulse" data-id="element-1712" />
                  <div className="h-3 bg-gray-200 rounded-full w-3/5 animate-pulse" data-id="element-1713" />
                </div>
              </> : <>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2" data-id="element-1714">
                  Cleaned Transcript
                </p>
                <p className="text-gray-700 text-sm leading-relaxed" data-id="element-1715">
                  {transcription && `"${transcription}"`}
                </p>
              </>}
          </div>

          {/* Missing Info Banner */}
          {missingInfo.length > 0 && hasRecorded && !isTranscribing && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full bg-amber-50 border border-amber-300 rounded-xl px-4 py-3" data-id="element-1716">
              <p className="text-amber-700 text-xs font-semibold mb-1" data-id="element-1717">
                Missing information
              </p>
              <p className="text-amber-600 text-xs mb-3" data-id="element-1718">
                Your description is missing:{' '}
                <span className="font-medium text-amber-700" data-id="element-1719">
                  {missingInfo.join(', ')}
                </span>
                . Add more details for a better PTP.
              </p>
              <div className="flex gap-2" data-id="element-1720">
                <button onClick={() => {
            setMissingInfo([]);
            appendModeRef.current = true;
            startRecording();
          }} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-black text-xs font-semibold rounded-lg" data-id="element-1721">
                  <MicIcon className="w-3 h-3" data-id="element-1722" />
                  Add More Info
                </button>
                <button onClick={() => setMissingInfo([])} className="px-3 py-1.5 text-amber-600 text-xs font-medium rounded-lg border border-amber-300" data-id="element-1723">
                  Dismiss
                </button>
              </div>
            </motion.div>}
        </div>}

      {/* Text Mode */}
      {mode === 'text' && <div className="flex-1 flex flex-col space-y-4" data-id="element-1724">
          <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 focus-within:border-yellow-400/50 transition-colors" data-id="element-1725">
            <textarea placeholder={t('voice.placeholder')} className="w-full h-full bg-transparent text-gray-700 text-sm placeholder-gray-400 resize-none outline-none leading-relaxed" value={textInput} onChange={e => {
          setTextInput(e.target.value);
          setTextAnalyzed(false);
          updateData({
            taskDescription: e.target.value
          });
        }} autoFocus disabled={isAnalyzingText} data-id="element-1726" />
          </div>

          {isAnalyzingText && <div className="w-full p-4 rounded-xl bg-gray-50 border border-gray-200" data-id="element-1727">
              <p className="text-xs text-blue-600 uppercase tracking-wider font-semibold mb-3" data-id="element-1728">
                {t('voice.analyzing')}
              </p>
              <div className="space-y-2" data-id="element-1729">
                <div className="h-3 bg-gray-200 rounded-full w-full animate-pulse" data-id="element-1730" />
                <div className="h-3 bg-gray-200 rounded-full w-4/5 animate-pulse" data-id="element-1731" />
                <div className="h-3 bg-gray-200 rounded-full w-3/5 animate-pulse" data-id="element-1732" />
              </div>
            </div>}

          {textAnalyzed && !isAnalyzingText && <div className="w-full p-4 rounded-xl bg-green-50 border border-green-200" data-id="element-1733">
              <p className="text-xs text-green-600 uppercase tracking-wider font-semibold mb-1" data-id="element-1734">
                ✓ {t('summary.aiComplete')}
              </p>
            </div>}

          {/* Missing Info Banner — same as voice mode */}
          {missingInfo.length > 0 && textAnalyzed && !isAnalyzingText && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full bg-amber-50 border border-amber-300 rounded-xl px-4 py-3" data-id="element-1735">
              <p className="text-amber-700 text-xs font-semibold mb-1" data-id="element-1736">
                {t('voice.missingTitle')}
              </p>
              <p className="text-amber-600 text-xs mb-3" data-id="element-1737">
                {t('voice.missingPrefix')}{' '}
                <span className="font-medium text-amber-700" data-id="element-1738">
                  {missingInfo.join(', ')}
                </span>
                {t('voice.missingSuffix')}
              </p>
              <button onClick={() => setMissingInfo([])} className="px-3 py-1.5 text-amber-600 text-xs font-medium rounded-lg border border-amber-300" data-id="element-1739">
                {t('dismiss')}
              </button>
            </motion.div>}

          <p className="text-gray-400 text-xs text-right" data-id="element-1740">
            {textInput.length > 0 ? `${textInput.length} characters` : 'Start typing...'}
          </p>
        </div>}

      {/* Upload Mode */}
      {mode === 'upload' && <div className="flex-1 flex flex-col items-center justify-center space-y-4" data-id="element-1741">
          <div className="w-full border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center text-center" data-id="element-1742">
            <FileUpIcon className="w-10 h-10 text-gray-400 mb-3" data-id="element-1743" />
            <p className="text-gray-500 text-sm font-medium mb-1" data-id="element-1744">
              Upload a work document
            </p>
            <p className="text-gray-400 text-xs" data-id="element-1745">
              PDF, Word, or image of a work order, scope, or method statement
            </p>
          </div>
          <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 flex items-center gap-2 w-full" data-id="element-1746">
            <FileUpIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-1747" />
            <p className="text-amber-700 text-xs" data-id="element-1748">
              Document upload is available in the full version.
            </p>
          </div>
        </div>}

      <div className="sticky bottom-0 z-10 pt-3 pb-2 bg-gray-50" data-id="element-1749">
        <PrimaryButton onClick={() => {
        if (mode === 'text' && !textAnalyzed && textInput.trim().length > 0) {
          analyzeTextInput().then(() => {});
        } else {
          onNext();
        }
      }} disabled={isTranscribing || isAnalyzingText || mode === 'voice' && (isRecording || !hasRecorded) || mode === 'text' && textInput.trim().length === 0 || mode === 'upload'} data-id="element-1750">
          {isAnalyzingText ? t('voice.analyzing') : mode === 'text' && !textAnalyzed && textInput.trim().length > 0 ? t('voice.analyzeTask') : textAnalyzed ? t('continue') : t('voice.analyzeTask')}
        </PrimaryButton>
      </div>
    </div>;
}